import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Users, Fuel, Cog, MapPin, Phone, Search, Clock, Info, DoorOpen, Gauge, ShieldCheck,
  ChevronDown, Plane, Lock, Zap, BadgeCheck, PhoneCall, Route, Banknote, ShieldAlert, CalendarDays,
  FileText, Baby, Navigation, UserPlus, Truck, Mail, ArrowRight, RefreshCw, Star, X,
} from "lucide-react";
import ShopTunnel from "../components/ShopTunnel";
import ShopFooter from "../components/ShopFooter";
import ShopHeader from "../components/ShopHeader";
import ShopModal from "../components/ShopModal";
import { getShopTheme } from "../components/shopTheme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/e039693c8e877ed7c24d76214fb1752d085e566f117ef3d4373d31f6edc159d8.png";
const DAY_LABELS = { monday: "Lun", tuesday: "Mar", wednesday: "Mer", thursday: "Jeu", friday: "Ven", saturday: "Sam", sunday: "Dim" };
const HOURS_OPTS = Array.from({ length: 15 }, (_, i) => `${String(i + 7).padStart(2, "0")}:00`);

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(28px)", transition: `opacity .8s cubic-bezier(.22,1,.36,1) ${delay}ms, transform .8s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

function HoursCompact({ hours, T }) {
  if (!hours) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2">
      {Object.entries(DAY_LABELS).map(([k, lbl]) => {
        const h = hours[k];
        if (!h) return null;
        return (
          <div key={k} className={`flex justify-between text-[11px] ${T.muted}`}>
            <span className="font-medium">{lbl}</span>
            <span>{h.closed ? "Fermé" : `${h.open}–${h.close}`}</span>
          </div>
        );
      })}
    </div>
  );
}

const CONDITION_ICONS = {
  min_driver_age: Users, min_license_years: FileText, deposit: Banknote, franchise: ShieldAlert,
  included_km_per_day: Gauge, extra_km_price: Route, min_duration_days: CalendarDays, max_duration_days: CalendarDays,
};
const OPTION_ICONS = {
  child_seat: Baby, gps: Navigation, secondary_driver: UserPlus, delivery: Truck,
  one_way: Route, extra_km: Gauge, after_hours: Clock, airport: Plane,
};

export default function Shop() {
  const { slug } = useParams();
  const pt = new URLSearchParams(window.location.search).get("pt") || "";
  const [shop, setShop] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(null);
  const [modal, setModal] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const [q, setQ] = useState({ start_date: today, start_time: "09:00", end_date: today, end_time: "18:00", pickup: "", dropoff: "" });
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [filters, setFilters] = useState({ category: "", transmission: "", fuel: "", sort: "" });
  const [openFaq, setOpenFaq] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const setQv = (k, v) => setQ((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("avis")) setReviewOpen(true);
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/shop/${slug}`, { params: pt ? { pt } : {} })
      .then(({ data }) => {
        setShop(data);
        const cities = (data.cities || []).join(", ");
        document.title = cities ? `Location de voiture à ${cities} — ${data.name}` : `${data.name} — Réservation en ligne`;
        const iconUrl = data.favicon || data.logo;
        if (iconUrl) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }
          link.href = iconUrl;
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const runSearch = async () => {
    setSearching(true);
    setSearchError("");
    try {
      const { data } = await axios.get(`${API}/public/shop/${slug}/search`, { params: { start_date: q.start_date, end_date: q.end_date, ...(pt ? { pt } : {}) } });
      setResults(data);
      setTimeout(() => document.getElementById("vehicules")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      setSearchError(e.response?.data?.detail || "Recherche impossible");
      setResults(null);
    }
    setSearching(false);
  };

  const baseVehicles = useMemo(() => (results ? results.vehicles : shop?.vehicles || []), [results, shop]);
  const distinct = (key) => [...new Set(baseVehicles.map((v) => v[key]).filter(Boolean))];
  const transmissions = useMemo(() => distinct("transmission"), [baseVehicles]); // eslint-disable-line
  const fuels = useMemo(() => distinct("fuel"), [baseVehicles]); // eslint-disable-line
  const cats = shop?.categories || [];
  const catLabelMap = shop?.category_labels || {};
  const catLabel = (c) => catLabelMap[c] || (/^cat_/i.test(String(c || "")) ? "" : c);
  const showFilters = cats.length > 1 || transmissions.length > 1 || fuels.length > 1;

  const vehicles = useMemo(() => {
    let list = [...baseVehicles];
    if (filters.category) list = list.filter((v) => v.category === filters.category);
    if (filters.transmission) list = list.filter((v) => v.transmission === filters.transmission);
    if (filters.fuel) list = list.filter((v) => v.fuel === filters.fuel);
    if (filters.sort === "asc") list.sort((a, b) => (a.daily_price || a.daily_price_from || 0) - (b.daily_price || b.daily_price_from || 0));
    if (filters.sort === "desc") list.sort((a, b) => (b.daily_price || b.daily_price_from || 0) - (a.daily_price || a.daily_price_from || 0));
    return list;
  }, [baseVehicles, filters]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm" data-testid="shop-not-found">Cette page n'existe pas ou n'est plus disponible.</div>;
  if (!shop) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const T = getShopTheme(shop.theme);
  const dark = T.dark;
  const locs = shop.locations || [];
  const hasConditions = Object.keys(shop.conditions || {}).length > 0;
  const hasOptions = (shop.options || []).length > 0;
  const hasFaq = (shop.faq || []).length > 0;
  const multiAgency = locs.length > 1;
  const pickupLoc = locs.find((l) => l.id === q.pickup) || locs[0];
  const phone = shop.contact?.phone || shop.phone;
  const wa = shop.contact?.whatsapp;
  const allVehicles = shop.vehicles || [];
  const catCounts = cats.map((c) => ({ cat: c, n: allVehicles.filter((v) => v.category === c).length })).filter((x) => x.n > 0);

  const fieldLabel = `text-[10px] font-bold uppercase tracking-wider mb-1.5 block ${T.muted}`;
  const sectionTitle = "text-2xl sm:text-3xl font-semibold tracking-tight";
  const bigInput = `${T.input} !h-12`;

  const spec = (Icon, txt, testid) => txt != null && txt !== "" && (
    <span key={testid} className={`inline-flex items-center gap-1.5 text-xs ${T.muted}`}><Icon className="h-3.5 w-3.5" /> {txt}</span>
  );

  const openVerified = () => setModal({
    key: "verified", icon: BadgeCheck, title: "Entreprise vérifiée par Junago",
    body: (
      <>
        <p>Cette entreprise utilise <span className="font-semibold">Junago</span> comme logiciel de gestion de flotte.</p>
        <p className="mt-3">Les disponibilités, les véhicules, les tarifs et les agences affichés sur cette page sont synchronisés directement depuis son logiciel de gestion. Les informations sont mises à jour automatiquement.</p>
        <ul className="mt-4 space-y-2">
          {[
            "Identité du gérant vérifiée",
            "Immatriculation de la société (KBIS) contrôlée",
            ...(shop.insurance ? [`Attestation d'assurance fournie (${shop.insurance.insurer})`] : []),
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-sm font-medium">
              <BadgeCheck className={`h-4 w-4 mt-0.5 shrink-0 ${dark ? "text-emerald-300" : "text-emerald-600"}`} /> {t}
            </li>
          ))}
        </ul>
      </>
    ),
  });

  const reassurance = [
    { key: "sync", icon: RefreshCw, label: "Disponibilités synchronisées en temps réel", heroLabel: "Disponibilités en temps réel", show: true, body: <p>Cette page est directement connectée au logiciel de gestion de flotte du loueur. Les véhicules affichés, leurs tarifs et leurs disponibilités sont synchronisés automatiquement — ce que vous voyez est à jour.</p> },
    { key: "secure", icon: Lock, label: "Réservation sécurisée", show: shop.stripe_enabled, body: <p>Votre paiement est chiffré (SSL) et traité par <span className="font-semibold">Stripe</span>, leader mondial du paiement en ligne. Le loueur n'a jamais accès à vos données bancaires.</p> },
    { key: "instant", icon: Zap, label: "Confirmation immédiate", show: shop.stripe_enabled, body: <p>Dès votre paiement validé, votre réservation est confirmée instantanément et enregistrée dans le planning du loueur. Vous recevez une confirmation par email.</p> },
    { key: "assist", icon: PhoneCall, label: "Assistance", show: !!(phone || wa), body: <p>Une question avant ou pendant votre location ? Le loueur vous répond directement{phone ? ` par téléphone (${phone})` : ""}{wa ? `${phone ? " ou" : ""} sur WhatsApp` : ""}.</p> },
    { key: "verified", icon: BadgeCheck, label: "Loueur vérifié par Junago", heroLabel: "Entreprise vérifiée", show: shop.verified, onClick: openVerified },
  ].filter((r) => r.show);
  const HERO_KEYS = ["sync", "assist", "verified"];
  const heroBadges = reassurance.filter((r) => HERO_KEYS.includes(r.key));
  const bandItems = reassurance.filter((r) => !HERO_KEYS.includes(r.key));

  const trustItems = [
    shop.verified && { key: "verified", icon: BadgeCheck, title: "Loueur vérifié", desc: "Identité et immatriculation (KBIS) contrôlées", badge: "Loueur vérifié", onClick: openVerified },
    shop.insurance && { key: "insurance", icon: ShieldCheck, title: "Véhicules assurés", desc: `Flotte assurée${shop.insurance.coverage ? ` ${shop.insurance.coverage.toLowerCase()}` : ""} par ${shop.insurance.insurer}` },
    (phone || wa) && { key: "contact", icon: PhoneCall, title: "Assistance directe", desc: "Réponse rapide par téléphone ou WhatsApp" },
    shop.stripe_enabled && { key: "instant", icon: Zap, title: "Réservation instantanée", desc: "Confirmation immédiate après paiement" },
  ].filter(Boolean);

  return (
    <div className={`min-h-screen overflow-x-hidden ${T.page}`} data-testid="shop-page" style={{ scrollBehavior: "smooth" }}>
      {pt && (
        <div className="sticky top-0 z-[60] bg-amber-500 text-white text-center text-[11px] font-bold py-1.5 px-4" data-testid="shop-preview-banner">
          Mode aperçu — cette vue est privée. Les paiements de test ne sont pas recommandés depuis l'aperçu.
        </div>
      )}
      <ShopHeader shop={shop} T={T} onCategory={(cat) => setFilters((p) => ({ ...p, category: cat }))} />

      {/* ═══ HERO ═══ */}
      <section id="accueil" className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-8 scroll-mt-16 overflow-visible">
        <div className={`pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full blur-[120px] opacity-30 ${dark ? "bg-violet-600" : "bg-amber-200"}`} />
        <div className="grid lg:grid-cols-[1fr,440px] gap-10 lg:gap-14 items-center">
          <div style={{ animation: "jnFadeUp .8s cubic-bezier(.22,1,.36,1) both" }}>
            {heroBadges.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="hero-badges">
                {heroBadges.map((r) => (
                  <button key={r.key} onClick={r.onClick || (() => setModal({ key: r.key, icon: r.icon, title: r.label, body: r.body }))}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-transform hover:scale-[1.03] ${dark ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                    data-testid={`hero-badge-${r.key}`}>
                    <r.icon className="h-3.5 w-3.5" /> {r.heroLabel || r.label} <Info className="h-3 w-3 opacity-60" />
                  </button>
                ))}
              </div>
            )}
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] ${T.h1Extra || ""}`} style={T.h1Style}>
              Location de voiture{shop.cities?.length ? ` à ${shop.cities[0]}` : ""},<br />simple et sécurisée
            </h1>
            <p className={`mt-5 text-base sm:text-lg max-w-xl leading-relaxed ${T.muted}`}>
              {shop.name} — réservez en ligne en 2 minutes. Choisissez vos dates{multiAgency ? ", votre agence" : ""}, payez en toute sécurité, et prenez la route.
            </p>
          </div>
          <div className="hidden lg:block relative" style={{ animation: "jnFadeUp .8s cubic-bezier(.22,1,.36,1) .15s both" }}>
            <div className={`absolute -inset-6 rounded-[40px] blur-3xl opacity-25 ${dark ? "bg-gradient-to-br from-violet-600 to-fuchsia-600" : "bg-gradient-to-br from-amber-300 to-orange-200"}`} />
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl aspect-[4/3]" data-testid="hero-lifestyle">
              <img src={HERO_IMG} alt="Remise des clés de votre véhicule" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
              {allVehicles.length > 0 && (
                <div className="absolute bottom-4 left-4 px-3.5 py-2 rounded-2xl bg-white/90 backdrop-blur text-slate-900 text-xs font-bold shadow-lg">
                  {allVehicles.length} véhicule{allVehicles.length > 1 ? "s" : ""} disponible{allVehicles.length > 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Moteur de réservation */}
        <div className="relative mt-12" style={{ animation: "jnFadeUp .8s cubic-bezier(.22,1,.36,1) .25s both" }}>
          <div className={`pointer-events-none absolute -inset-4 rounded-[40px] blur-2xl opacity-20 ${dark ? "bg-violet-600" : "bg-slate-300"}`} />
          <div id="reservation" className={`relative rounded-[28px] p-6 sm:p-9 scroll-mt-24 ${T.searchCard}`} data-testid="shop-search-module">
            <div className={`text-[11px] font-bold uppercase tracking-widest mb-5 ${T.muted}`}>Réservez votre véhicule</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <span className={fieldLabel}>Départ</span>
                <div className="flex gap-2">
                  <input type="date" min={today} value={q.start_date} onChange={(e) => setQv("start_date", e.target.value)} className={`${bigInput} flex-1 min-w-[7rem]`} data-testid="search-start-date" />
                  <select value={q.start_time} onChange={(e) => setQv("start_time", e.target.value)} className={`${bigInput} w-[5.75rem] shrink-0 px-2`} data-testid="search-start-time">
                    {HOURS_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <span className={fieldLabel}>Retour</span>
                <div className="flex gap-2">
                  <input type="date" min={q.start_date} value={q.end_date} onChange={(e) => setQv("end_date", e.target.value)} className={`${bigInput} flex-1 min-w-[7rem]`} data-testid="search-end-date" />
                  <select value={q.end_time} onChange={(e) => setQv("end_time", e.target.value)} className={`${bigInput} w-[5.75rem] shrink-0 px-2`} data-testid="search-end-time">
                    {HOURS_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
              {multiAgency ? (
                <>
                  <div>
                    <span className={fieldLabel}>Agence de départ</span>
                    <select value={q.pickup} onChange={(e) => setQv("pickup", e.target.value)} className={bigInput} data-testid="search-pickup">
                      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className={fieldLabel}>Agence de retour</span>
                    <select value={q.dropoff} onChange={(e) => setQv("dropoff", e.target.value)} className={bigInput} data-testid="search-dropoff">
                      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </>
              ) : locs.length === 1 ? (
                <div className="lg:col-span-2">
                  <span className={fieldLabel}>Agence</span>
                  <div className={`h-12 px-3 rounded-xl flex items-center gap-2 text-sm font-medium ${dark ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`} data-testid="search-single-agency">
                    <MapPin className={`h-4 w-4 ${T.muted}`} /> {locs[0].name}
                  </div>
                </div>
              ) : null}
            </div>
            {searchError && <div className="mt-3 text-xs font-medium text-red-500" data-testid="search-error">{searchError}</div>}
            <button onClick={runSearch} disabled={searching}
              className={`mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 h-14 text-sm font-bold transition-all hover:scale-[1.02] disabled:opacity-50 ${T.btn}`}
              data-testid="search-submit">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Rechercher un véhicule
            </button>
          </div>
        </div>

        {/* Bande de réassurance */}
        {bandItems.length > 0 && (
          <div className="mt-8 flex flex-wrap justify-center gap-x-2 gap-y-2" data-testid="reassurance-badges" style={{ animation: "jnFadeUp .8s cubic-bezier(.22,1,.36,1) .35s both" }}>
            {bandItems.map((r) => (
              <button key={r.key} onClick={r.onClick || (() => setModal({ key: r.key, icon: r.icon, title: r.label, body: r.body }))}
                className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[11px] font-semibold transition-colors ${dark ? "bg-white/[0.04] text-slate-300 hover:bg-white/10" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"}`}
                data-testid={`reassurance-${r.key}`}>
                <BadgeCheck className={`h-3.5 w-3.5 ${dark ? "text-emerald-300" : "text-emerald-600"}`} /> {r.label}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ═══ DISPONIBILITÉ AUJOURD'HUI ═══ */}
      {allVehicles.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
          <Reveal>
            <div className={`rounded-[28px] p-7 sm:p-9 ${T.band}`} data-testid="availability-block">
              <div className="flex flex-wrap items-center justify-between gap-6">
                <div>
                  <div className={`text-[11px] font-bold uppercase tracking-widest ${T.muted}`}>Disponibilité aujourd'hui</div>
                  <div className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">{allVehicles.length} véhicule{allVehicles.length > 1 ? "s" : ""} disponible{allVehicles.length > 1 ? "s" : ""}</div>
                </div>
                {catCounts.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {catCounts.map(({ cat, n }) => (
                      <a key={cat} href="#vehicules" onClick={() => setFilters((p) => ({ ...p, category: cat }))}
                        className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs font-bold transition-all hover:scale-[1.04] ${T.card}`}
                        data-testid={`availability-cat-${cat}`}>
                        <span className={`inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-full text-[10px] font-black ${T.accentBox}`}>{n}</span> {catLabel(cat)}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* ═══ VÉHICULES ═══ */}
      <section id="vehicules" className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16 scroll-mt-16">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <h2 className={sectionTitle} data-testid="vehicles-title">
                {results ? `${vehicles.length} véhicule${vehicles.length > 1 ? "s" : ""} disponible${vehicles.length > 1 ? "s" : ""}` : "Nos véhicules"}
              </h2>
              {results && <div className={`text-xs mt-1.5 ${T.muted}`}>Du {q.start_date} {q.start_time} au {q.end_date} {q.end_time} · {results.days} jour{results.days > 1 ? "s" : ""}</div>}
            </div>
            {showFilters && (
              <div className="flex flex-wrap gap-2" data-testid="vehicles-filters">
                {cats.length > 1 && (
                  <select value={filters.category} onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))} className={`${T.input} !h-9 !w-auto text-xs`} data-testid="filter-category">
                    <option value="">Catégorie</option>
                    {cats.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                  </select>
                )}
                {transmissions.length > 1 && (
                  <select value={filters.transmission} onChange={(e) => setFilters((p) => ({ ...p, transmission: e.target.value }))} className={`${T.input} !h-9 !w-auto text-xs`} data-testid="filter-transmission">
                    <option value="">Boîte</option>
                    {transmissions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                {fuels.length > 1 && (
                  <select value={filters.fuel} onChange={(e) => setFilters((p) => ({ ...p, fuel: e.target.value }))} className={`${T.input} !h-9 !w-auto text-xs`} data-testid="filter-fuel">
                    <option value="">Carburant</option>
                    {fuels.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                )}
                <select value={filters.sort} onChange={(e) => setFilters((p) => ({ ...p, sort: e.target.value }))} className={`${T.input} !h-9 !w-auto text-xs`} data-testid="filter-sort">
                  <option value="">Prix</option>
                  <option value="asc">Croissant</option>
                  <option value="desc">Décroissant</option>
                </select>
              </div>
            )}
          </div>
        </Reveal>

        {vehicles.length === 0 ? (
          <div className={`rounded-2xl border border-dashed py-16 text-center text-sm ${dark ? "border-white/10 text-slate-500" : "border-slate-200 text-slate-400"}`} data-testid="vehicles-empty">
            {results ? "Aucun véhicule disponible sur ces dates." : "Aucun véhicule disponible pour le moment."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v, i) => (
              <Reveal key={v.id} delay={Math.min(i * 80, 320)}>
                <div className={`group rounded-[24px] overflow-hidden flex flex-col transition-all duration-300 ${T.card} ${T.hover}`} data-testid={`shop-vehicle-${v.id}`}>
                  <div className="relative aspect-[16/11] overflow-hidden">
                    {v.photos?.[0] ? (
                      <img src={v.photos[0]} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700" />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center text-xs ${dark ? "bg-white/5 text-slate-600" : "bg-slate-100 text-slate-300"}`}>Photo à venir</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
                    {v.category_label && <span className="absolute top-3.5 left-3.5 px-2.5 py-1 rounded-full text-[10px] font-bold backdrop-blur bg-white/90 text-slate-800">{v.category_label}</span>}
                    {shop.insurance && (
                      <span className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold backdrop-blur bg-emerald-500/90 text-white" data-testid={`vehicle-insured-${v.id}`}>
                        <ShieldCheck className="h-2.5 w-2.5" /> Assuré
                      </span>
                    )}
                    <div className="absolute bottom-3.5 left-4 text-white drop-shadow" data-testid={`vehicle-price-${v.id}`}>
                      {results ? (
                        v.daily_price != null ? (
                          <>
                            <span className="text-2xl font-extrabold">{v.total} €</span>
                            <span className="text-[11px] font-semibold opacity-90"> · {v.daily_price} €/jour</span>
                          </>
                        ) : (
                          <span className="text-sm font-bold bg-black/40 px-2 py-1 rounded-lg">Tarif non configuré</span>
                        )
                      ) : (
                        v.daily_price_from != null ? (
                          <>
                            <span className="text-[10px] font-semibold opacity-90 block leading-none mb-0.5">À partir de</span>
                            <span className="text-2xl font-extrabold leading-none">{v.daily_price_from} €</span>
                            <span className="text-xs font-semibold opacity-90"> /jour</span>
                          </>
                        ) : (
                          <span className="text-sm font-bold bg-black/40 px-2 py-1 rounded-lg">Tarif non configuré</span>
                        )
                      )}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="text-base font-bold">{v.name || `${v.brand} ${v.model}`}</div>
                    <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
                      {spec(Users, v.seats && `${v.seats} places`, "seats")}
                      {spec(Cog, v.transmission, "transmission")}
                      {spec(Fuel, v.fuel, "fuel")}
                      {spec(DoorOpen, v.doors && `${v.doors} portes`, "doors")}
                      {spec(Gauge, shop.included_km_per_day && `${shop.included_km_per_day} km/j`, "km")}
                    </div>
                    <button onClick={() => setBooking(v)} data-testid={`shop-book-${v.id}`}
                      className={`mt-5 w-full h-12 text-sm font-bold transition-all hover:scale-[1.02] ${T.btn}`}>
                      Réserver ce véhicule
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* ═══ GARANTIES ═══ */}
      {trustItems.length > 0 && (
        <section id="garanties" className={`py-16 sm:py-24 scroll-mt-16 ${T.band}`} data-testid="trust-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-8">
            <Reveal>
              <h2 className={`${sectionTitle} mb-2`}>Louez en toute confiance</h2>
              <p className={`text-sm mb-10 ${T.muted}`}>Des garanties concrètes, à chaque étape de votre location.</p>
            </Reveal>
            {shop.stripe_enabled && (
              <Reveal>
                <div className={`mb-5 rounded-[22px] p-6 sm:p-8 ${T.card}`} data-testid="payment-security-card">
                  <div className="grid lg:grid-cols-[1fr,auto] gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-3.5">
                        <span className={`inline-flex h-12 w-12 rounded-2xl items-center justify-center shrink-0 ${dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600"}`}><Lock className="h-6 w-6" /></span>
                        <div>
                          <div className="text-base font-bold">Sécurité des paiements</div>
                          <div className={`text-xs mt-0.5 ${T.muted}`}>Vos données bancaires sont protégées de bout en bout.</div>
                        </div>
                      </div>
                      <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                        {[
                          "Transactions chiffrées SSL / TLS 256 bits",
                          "Authentification bancaire 3D Secure",
                          "Conformité PCI DSS — paiements traités par Stripe",
                          "Aucune donnée bancaire stockée par le loueur",
                        ].map((t) => (
                          <div key={t} className={`flex items-start gap-2 text-xs font-medium ${T.muted}`} data-testid="payment-security-point">
                            <BadgeCheck className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${dark ? "text-emerald-300" : "text-emerald-600"}`} /> {t}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap lg:flex-col items-center justify-center gap-4">
                      <div className={`flex items-center gap-3 text-2xl sm:text-3xl ${dark ? "text-slate-300" : "text-slate-400"}`} data-testid="payment-card-logos">
                        <i className="fab fa-cc-visa" /><i className="fab fa-cc-mastercard" /><i className="fab fa-cc-amex" /><i className="fab fa-apple-pay" /><i className="fab fa-google-pay" />
                      </div>
                      <div className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs font-bold ${dark ? "bg-white/5 border border-white/10 text-slate-200" : "bg-slate-50 border border-slate-200 text-slate-700"}`} data-testid="payment-stripe-badge">
                        <Lock className="h-3.5 w-3.5 text-emerald-500" /> Paiement géré par <i className="fab fa-stripe text-3xl leading-none" />
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {trustItems.map((t, i) => (
                <Reveal key={t.key} delay={i * 90}>
                  <div onClick={t.onClick} className={`rounded-[22px] p-6 h-full transition-all ${T.card} ${t.onClick ? "cursor-pointer hover:-translate-y-1" : ""}`} data-testid={`trust-${t.key}`}>
                    <span className={`inline-flex h-11 w-11 rounded-2xl items-center justify-center mb-4 ${T.accentBox}`}><t.icon className="h-5 w-5" /></span>
                    <div className="text-sm font-bold">{t.title}</div>
                    <div className={`text-xs mt-1.5 leading-relaxed ${T.muted}`}>{t.desc}</div>
                    {t.badge && (
                      <span className={`mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${dark ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-emerald-600 text-white"}`} data-testid="trust-verified-badge">
                        <BadgeCheck className="h-3 w-3" /> {t.badge}
                      </span>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ AVIS CLIENTS ═══ */}
      {(shop.reviews || []).length > 0 && (
        <section id="avis" className={`py-16 sm:py-24 scroll-mt-16 ${T.band}`} data-testid="shop-reviews">
          <div className="max-w-6xl mx-auto px-4 sm:px-8">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                  <h2 className={`${sectionTitle} mb-2`}>Ils nous ont fait confiance</h2>
                  <p className={`text-sm ${T.muted}`}>Des avis authentiques laissés par nos clients.</p>
                </div>
                {shop.rating_avg && (
                  <div className={`inline-flex items-center gap-2 px-4 h-11 rounded-full ${T.card}`} data-testid="shop-rating-avg">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-bold">{shop.rating_avg}/5</span>
                    <span className={`text-xs ${T.muted}`}>· {shop.rating_count} avis</span>
                  </div>
                )}
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(shop.reviews || []).slice(0, 6).map((r, i) => (
                <Reveal key={r.id} delay={i * 80}>
                  <div className={`rounded-[22px] p-6 h-full ${T.card}`} data-testid={`shop-review-${r.id}`}>
                    <div className="flex items-center gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "fill-amber-400 text-amber-400" : dark ? "text-slate-700" : "text-slate-200"}`} />
                      ))}
                    </div>
                    {r.comment && <p className={`text-sm leading-relaxed mb-4 ${T.muted}`}>« {r.comment} »</p>}
                    <div className="text-xs font-bold">{r.name}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ AGENCES ═══ */}
      {locs.length > 0 && (
        <section id="agences" className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 scroll-mt-16" data-testid="shop-agencies">
          <Reveal>
            <h2 className={`${sectionTitle} mb-2`}>Nos agences</h2>
            <p className={`text-sm mb-10 ${T.muted}`}>Récupérez et restituez votre véhicule où cela vous arrange.</p>
          </Reveal>
          <div className={`grid gap-6 ${locs.length === 1 ? "grid-cols-1" : locs.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {locs.map((l, i) => (
              <Reveal key={l.id} delay={i * 90}>
                <div className={`rounded-[22px] p-6 h-full ${locs.length <= 2 && l.lat != null ? "grid md:grid-cols-[1fr,minmax(220px,45%)] gap-6" : ""} ${T.card}`} data-testid={`shop-agency-${l.id}`}>
                  <div>
                  <div className="flex items-center gap-3">
                    <span className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${T.accentBox}`}><MapPin className="h-5 w-5" /></span>
                    <div>
                      <div className="text-sm font-bold">{l.name}</div>
                      {l.airport && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${dark ? "bg-sky-500/15 text-sky-300" : "bg-sky-50 text-sky-700"}`}><Plane className="h-2.5 w-2.5" /> {l.airport}</span>}
                    </div>
                  </div>
                  {l.address && <div className={`text-xs mt-3.5 ${T.muted}`}>{l.address}</div>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {phone && (
                      <a href={`tel:${phone}`} className={`inline-flex items-center gap-1.5 px-4 h-9 text-[11px] font-bold transition-colors ${T.ghost}`} data-testid={`agency-call-${l.id}`}>
                        <Phone className="h-3 w-3" /> Appeler
                      </a>
                    )}
                    {l.address && (
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(l.address)}`} target="_blank" rel="noreferrer"
                        className={`inline-flex items-center gap-1.5 px-4 h-9 text-[11px] font-bold transition-colors ${T.ghost}`} data-testid={`agency-route-${l.id}`}>
                        <Navigation className="h-3 w-3" /> Voir l'itinéraire
                      </a>
                    )}
                  </div>
                  {l.hours && (
                    <div className="mt-4">
                      <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${T.muted}`}><Clock className="h-3 w-3" /> Horaires</div>
                      <HoursCompact hours={l.hours} T={T} />
                    </div>
                  )}
                  {l.access_info && (
                    <div className={`mt-4 rounded-xl p-3 text-[11px] leading-relaxed flex items-start gap-2 ${dark ? "bg-white/5 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                      <Info className="h-3 w-3 mt-0.5 shrink-0" /> {l.access_info}
                    </div>
                  )}
                  </div>
                  {l.lat != null && l.lng != null && (
                    <div className={`mt-5 ${locs.length <= 2 ? "md:mt-0" : ""} rounded-2xl overflow-hidden ${locs.length <= 2 ? "min-h-[220px]" : "aspect-[16/8]"} ${dark ? "border border-white/10" : "border border-slate-200"}`} data-testid={`agency-map-${l.id}`}>
                      <iframe
                        title={`Carte ${l.name}`}
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${l.lng - 0.008},${l.lat - 0.004},${l.lng + 0.008},${l.lat + 0.004}&layer=mapnik&marker=${l.lat},${l.lng}`}
                        className="w-full h-full border-0"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ═══ CONDITIONS + ASSURANCE ═══ */}
      {(hasConditions || shop.insurance) && (
        <section id="conditions" className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 scroll-mt-16" data-testid="shop-conditions">
          <Reveal>
            <h2 className={`${sectionTitle} mb-10`}>Conditions & garanties</h2>
          </Reveal>
          {shop.insurance && (
            <Reveal>
              <div className={`mb-6 rounded-[22px] p-6 flex items-start gap-4 ${T.card}`} data-testid="insurance-card">
                <span className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600"}`}><ShieldCheck className="h-6 w-6" /></span>
                <div>
                  <div className="text-sm font-bold">Véhicules assurés{shop.insurance.coverage ? ` — ${shop.insurance.coverage}` : ""}</div>
                  <div className={`text-xs mt-1 leading-relaxed ${T.muted}`}>Flotte couverte par <span className="font-semibold">{shop.insurance.insurer}</span>{shop.insurance.description ? ` · ${shop.insurance.description}` : ""}</div>
                </div>
              </div>
            </Reveal>
          )}
          {hasConditions && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["min_driver_age", "Âge minimum", (v) => `${v} ans`],
                ["min_license_years", "Permis requis", (v) => `${v} an${v > 1 ? "s" : ""} minimum`],
                ["deposit", "Caution", (v) => `${v} €`],
                ["franchise", "Franchise", (v) => `${v} €`],
                ["included_km_per_day", "Kilométrage inclus", (v) => `${v} km / jour`],
                ["extra_km_price", "Km supplémentaire", (v) => `${v} € / km`],
                ["min_duration_days", "Durée minimale", (v) => `${v} jour${v > 1 ? "s" : ""}`],
                ["max_duration_days", "Durée maximale", (v) => `${v} jours`],
                ["cancellation_notice_hours", "Annulation", (v) => `Jusqu'à ${v} h avant le départ`],
              ].filter(([k]) => shop.conditions[k] != null).map(([k, lbl, fmt], i) => {
                const Icon = CONDITION_ICONS[k] || FileText;
                return (
                  <Reveal key={k} delay={i * 60}>
                    <div className={`rounded-[20px] p-5 h-full ${T.card}`} data-testid={`condition-${k}`}>
                      <span className={`inline-flex h-9 w-9 rounded-xl items-center justify-center mb-3 ${T.accentBox}`}><Icon className="h-4 w-4" /></span>
                      <div className={`text-[10px] font-bold uppercase tracking-wider ${T.muted}`}>{lbl}</div>
                      <div className="text-sm font-semibold mt-1">{fmt(shop.conditions[k])}</div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ═══ OPTIONS ═══ */}
      {hasOptions && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="shop-options">
          <Reveal>
            <h2 className={`${sectionTitle} mb-10`}>Options disponibles</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {shop.options.map((o, i) => {
              const Icon = OPTION_ICONS[o.key] || FileText;
              return (
                <Reveal key={o.key} delay={i * 60}>
                  <div className={`rounded-[20px] p-5 h-full flex items-start gap-3.5 ${T.card}`} data-testid={`option-${o.key}`}>
                    <span className={`inline-flex h-10 w-10 rounded-xl items-center justify-center shrink-0 ${T.accentBox}`}><Icon className="h-5 w-5" /></span>
                    <div>
                      <div className="text-sm font-bold">{o.label}</div>
                      {o.detail && <div className={`text-[11px] mt-0.5 ${T.muted}`}>{o.detail}</div>}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══ FAQ ═══ */}
      {hasFaq && (
        <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-24 scroll-mt-16" data-testid="shop-faq">
          <Reveal>
            <h2 className={`${sectionTitle} mb-10 text-center`}>Questions fréquentes</h2>
          </Reveal>
          <div className="space-y-3">
            {shop.faq.map((f, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className={`rounded-[20px] ${T.card}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 p-5 text-left text-sm font-semibold">
                    {f.q}
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <div className={`px-5 pb-5 text-sm leading-relaxed ${T.muted}`}>{f.a}</div>}
                </div>
              </Reveal>
            ))}
          </div>
          {(phone || wa || shop.email) && (
            <Reveal delay={150}>
              <div className={`mt-10 rounded-[22px] p-7 text-center ${T.band}`} data-testid="faq-contact">
                <div className="text-sm font-bold">Vous ne trouvez pas votre réponse ?</div>
                <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                  {phone && <a href={`tel:${phone}`} className={`inline-flex items-center gap-2 px-5 h-10 text-xs font-bold ${T.ghost}`} data-testid="faq-contact-phone"><Phone className="h-3.5 w-3.5" /> Téléphone</a>}
                  {wa && <a href={`https://wa.me/${wa.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#25D366] text-white text-xs font-bold hover:opacity-90 transition-opacity" data-testid="faq-contact-whatsapp"><i className="fab fa-whatsapp" /> WhatsApp</a>}
                  {shop.email && <a href={`mailto:${shop.email}`} className={`inline-flex items-center gap-2 px-5 h-10 text-xs font-bold ${T.ghost}`} data-testid="faq-contact-email"><Mail className="h-3.5 w-3.5" /> Email</a>}
                </div>
              </div>
            </Reveal>
          )}
        </section>
      )}

      <ShopFooter shop={shop} dark={dark} T={T} cats={cats} catLabel={catLabel} onCategory={(cat) => setFilters((p) => ({ ...p, category: cat }))} onVerified={openVerified} />
      {reviewOpen && <ReviewModal slug={slug} dark={dark} onClose={() => setReviewOpen(false)} />}

      {/* Bouton flottant Appeler */}
      {phone && (
        <div className="fixed bottom-20 sm:bottom-6 right-4 z-40" data-testid="floating-contacts">
          <a href={`tel:${phone}`} className={`h-12 w-12 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform ${dark ? "bg-white text-slate-900" : "bg-slate-900 text-white"}`} data-testid="floating-call" aria-label="Appeler">
            <Phone className="h-5 w-5" />
          </a>
        </div>
      )}

      {/* Barre CTA mobile */}
      <div className={`sm:hidden fixed bottom-0 inset-x-0 z-40 flex items-center gap-2 px-3 py-2.5 ${dark ? "bg-[#0A0F1E] border-t border-white/10" : "bg-white border-t border-slate-200"}`} data-testid="mobile-cta-bar">
        {wa && (
          <a href={`https://wa.me/${wa.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 h-11 rounded-xl bg-[#25D366] text-white text-xs font-bold flex items-center justify-center gap-1.5"><i className="fab fa-whatsapp" /> WhatsApp</a>
        )}
        {phone && (
          <a href={`tel:${phone}`} className={`flex-1 h-11 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 ${dark ? "bg-white/10 text-white" : "bg-slate-100 text-slate-900"}`}><Phone className="h-3.5 w-3.5" /> Appeler</a>
        )}
        <a href="#reservation" className={`flex-1 h-11 text-xs font-bold flex items-center justify-center ${T.btn}`}>Réserver</a>
      </div>

      {modal && (
        <ShopModal title={modal.title} icon={modal.icon} dark={dark} onClose={() => setModal(null)} testid={`modal-${modal.key}`}>
          {modal.body || null}
        </ShopModal>
      )}

      {booking && (
        <ShopTunnel
          slug={slug}
          shop={shop}
          vehicle={booking}
          dark={dark}
          search={{ start_date: q.start_date, end_date: q.end_date, start_time: q.start_time, end_time: q.end_time, pickup_name: pickupLoc?.name }}
          onClose={() => setBooking(null)}
        />
      )}
      <style>{`@keyframes jnFadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

// ─── Modal de dépôt d'avis client ─────────────────────────────────────────────
function ReviewModal({ slug, dark, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", rating: 5, comment: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Votre nom est requis"); return; }
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/public/shop/${slug}/reviews`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible d'envoyer votre avis, réessayez.");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="shop-review-modal">
      <div className={`w-full max-w-md rounded-[24px] p-6 sm:p-8 shadow-2xl ${dark ? "bg-[#12121F] border border-white/10 text-white" : "bg-white text-slate-900"}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading text-lg font-semibold">{sent ? "Merci pour votre avis ! 🎉" : "Laissez-nous un avis"}</h3>
          <button onClick={onClose} className={`h-8 w-8 rounded-full flex items-center justify-center ${dark ? "hover:bg-white/10" : "hover:bg-slate-100"}`} data-testid="shop-review-close" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        {sent ? (
          <div className="mt-3">
            <p className={`text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-500"}`} data-testid="shop-review-thanks">
              Votre avis a bien été enregistré. Il sera affiché sur cette page très prochainement. À bientôt !
            </p>
            <button onClick={onClose} className="mt-5 w-full h-11 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors" data-testid="shop-review-done">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-3 space-y-4">
            <p className={`text-xs leading-relaxed ${dark ? "text-slate-400" : "text-slate-500"}`}>Votre retour aide d'autres clients à réserver en toute confiance.</p>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${dark ? "text-slate-400" : "text-slate-400"}`}>Votre note</label>
              <div className="mt-1.5 flex items-center gap-1" data-testid="shop-review-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, rating: s }))} className="p-0.5 transition-transform hover:scale-110" data-testid={`shop-review-star-${s}`} aria-label={`${s} étoiles`}>
                    <Star className={`h-7 w-7 ${s <= form.rating ? "fill-amber-400 text-amber-400" : dark ? "text-slate-700" : "text-slate-200"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${dark ? "text-slate-400" : "text-slate-400"}`}>Votre nom</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required
                className={`mt-1.5 w-full h-11 px-4 rounded-xl border text-sm outline-none focus:border-violet-400 ${dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
                placeholder="Jean Dupont" data-testid="shop-review-name" />
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${dark ? "text-slate-400" : "text-slate-400"}`}>Votre email (optionnel)</label>
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={`mt-1.5 w-full h-11 px-4 rounded-xl border text-sm outline-none focus:border-violet-400 ${dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
                placeholder="jean@exemple.fr" data-testid="shop-review-email" />
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${dark ? "text-slate-400" : "text-slate-400"}`}>Votre commentaire (optionnel)</label>
              <textarea value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} rows={3}
                className={`mt-1.5 w-full px-4 py-3 rounded-xl border text-sm outline-none resize-none focus:border-violet-400 ${dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
                placeholder="Service impeccable, véhicule nickel…" data-testid="shop-review-comment" />
            </div>
            {error && <div className="text-xs font-medium text-red-500" data-testid="shop-review-error">{error}</div>}
            <button type="submit" disabled={sending} className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="shop-review-submit">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer mon avis
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
